import styled from 'styled-components'
import { motion } from 'framer-motion'
import superheroesData from '../assets/data/superheroes.json'
import { AnimatedSection, fadeInUp } from './animations/AnimatedSection'

const getSdgColor = (id: number) => {
  const colors: { [key: number]: string } = {
    1: '#E5243B',
    2: '#DDA63A',
    3: '#4C9F38',
    4: '#C5192D',
    5: '#FF3A21',
    6: '#26BDE2',
    7: '#FCC30B',
    8: '#A21942',
    9: '#FD6925',
    10: '#DD1367',
    11: '#FD9D24',
    12: '#BF8B2E',
    13: '#3F7E44',
    14: '#0A97D9',
    15: '#56C02B',
    16: '#00689D',
    17: '#19486A',
  }
  return colors[id] || '#E82230'
}

const InfiniteHeroTicker = () => {
  // Multiply data to ensure seamless loop
  const tickerItems = [
    ...superheroesData,
    ...superheroesData,
    ...superheroesData,
  ]

  return (
    <Section>
      <HeaderContainer>
        <AnimatedSection variants={fadeInUp}>
          <SectionLabel>THE 17 GUARDIANS</SectionLabel>
        </AnimatedSection>
        <AnimatedSection variants={fadeInUp} delay={0.1}>
          <SectionTitle>
            Meet the SDG <HighlightGreen>Superheroes</HighlightGreen>
          </SectionTitle>
        </AnimatedSection>
      </HeaderContainer>

      <TickerWrapper>
        <TickerContent
          animate={{ x: '-33.3333%' }}
          transition={{
            duration: 80,
            repeat: Infinity,
            ease: 'linear',
          }}
        >
          {tickerItems.map((hero, idx) => (
            <HeroCard
              key={`${hero.id}-${idx}`}
              $sdgColor={getSdgColor(hero.id)}
            >
              <ImageWrapper>
                <HeroImage
                  src={`/images/heroes/SDG_${hero.id}.webp`}
                  alt={hero.name}
                />
              </ImageWrapper>
              <InfoOverlay>
                <HeroName>{hero.name}</HeroName>
                <SdgTag $color={getSdgColor(hero.id)}>SDG {hero.id}</SdgTag>
              </InfoOverlay>
            </HeroCard>
          ))}
        </TickerContent>
      </TickerWrapper>
    </Section>
  )
}

const Section = styled.section`
  width: 100%;
  padding: 80px 0;
  background-color: #f8f7f0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  gap: 48px;
`

const HeaderContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 0 24px;
`

const SectionLabel = styled.h3`
  font-family: 'Poppins', sans-serif;
  font-weight: 700;
  font-size: 14px;
  color: #607e03;
  letter-spacing: 0.1em;
  text-transform: uppercase;
`

const SectionTitle = styled.h2`
  font-family: 'Poppins', sans-serif;
  font-weight: 600;
  font-size: 48px;
  text-align: center;
  color: #000;
  @media (max-width: 768px) {
    font-size: 32px;
  }
`

const HighlightGreen = styled.span`
  background: linear-gradient(90deg, #0e6409 0%, #bbe148 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`

const TickerWrapper = styled.div`
  width: 100%;
  position: relative;
  mask-image: linear-gradient(
    to right,
    transparent,
    black 10%,
    black 90%,
    transparent
  );
`

const TickerContent = styled(motion.div)`
  display: flex;
  gap: 24px;
  width: fit-content;
`

const HeroCard = styled.div<{ $sdgColor: string }>`
  width: 280px;
  height: 380px;
  background: white;
  border-radius: 20px;
  border: 1px solid #eee;
  position: relative;
  overflow: hidden;
  flex-shrink: 0;
  transition: all 0.3s ease;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);

  &:hover {
    transform: translateY(-8px);
    border-color: ${(props) => props.$sdgColor};
    box-shadow: 0 12px 30px rgba(0, 0, 0, 0.1);
  }
`

const ImageWrapper = styled.div`
  width: 100%;
  height: 75%;
  background: #f9f9f9;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px;
`

const HeroImage = styled.img`
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
`

const InfoOverlay = styled.div`
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const HeroName = styled.h4`
  font-family: 'Poppins', sans-serif;
  font-weight: 600;
  font-size: 18px;
  margin: 0;
  color: #2a2a2a;
`

const SdgTag = styled.span<{ $color: string }>`
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  font-weight: 700;
  color: white;
  background: ${(props) => props.$color};
  padding: 4px 10px;
  border-radius: 100px;
  width: fit-content;
`

export const HeroStrip = () => {
  const stripItems = [
    ...superheroesData,
    ...superheroesData,
    ...superheroesData,
    ...superheroesData,
  ]

  return (
    <StripSection>
      <StripTickerWrapper>
        <StripTickerContent
          animate={{ x: '-25%' }}
          transition={{
            duration: 80,
            repeat: Infinity,
            ease: 'linear',
          }}
        >
          {stripItems.map((hero, idx) => (
            <StripItem key={`${hero.id}-${idx}`}>
              <StripImage
                src={`/images/heroes/SDG_${hero.id}.webp`}
                alt={hero.name}
                $color={getSdgColor(hero.id)}
              />
            </StripItem>
          ))}
        </StripTickerContent>
      </StripTickerWrapper>
    </StripSection>
  )
}

const StripSection = styled.div`
  width: 100%;
  padding: 64px 0;
  background: #ffffffff;
  overflow: hidden;
  position: relative;
  display: flex;
  align-items: center;

  @media (max-width: 768px) {
    padding: 40px 0;
  }
`

const StripTickerWrapper = styled.div`
  width: 100%;
  position: relative;
`

const StripTickerContent = styled(motion.div)`
  display: flex;
  gap: 100px;
  width: fit-content;
  align-items: center;
`

const StripItem = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
`

const StripImage = styled.img<{ $color: string }>`
  height: 140px;
  width: auto;
  object-fit: contain;
  filter: drop-shadow(0px 10px 15px ${({ $color }) => $color}80);
  transition: transform 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  user-select: none;
  pointer-events: none;
  -webkit-user-drag: none;

  @media (max-width: 768px) {
    height: 100px;
  }
  @media (max-width: 480px) {
    height: 80px;
  }
`

export default InfiniteHeroTicker
